import React, { useEffect, useState, useMemo, act } from "react";
import { useNavigate } from "react-router-dom";
import "../style/admin.css";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import jsPDF from "jspdf";
import "reactjs-popup/dist/index.css";
import CustomPopup from "./CustomPopup";
import { TextField, Autocomplete, Pagination } from "@mui/material";
import { createFilterOptions } from "@mui/material/Autocomplete";
import { AbstractAPI } from "../api";
import { ThreeDot } from "react-loading-indicators";
import IEW_logo from "../assets/images/IEW_logo.png";

function escapeRegExp(str = "") {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
export const formatBoldText = (text = "") => {
  if (!text) return text;

  const parts = text.split(/(\*\*.*?\*\*)/g);

  return parts.map((part, index) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      const clean = part.slice(2, -2); // remove **
      return <strong key={index}>{clean}</strong>;
    }
    return part;
  });
};


export const highlightText = (text, query) => {
  if (!query) return formatBoldText(text);

  const regex = new RegExp(`(${query})`, "gi");

  const parts = text.split(regex);

  return parts.map((part, index) => {
    if (part.toLowerCase() === query.toLowerCase()) {
      return (
        <mark key={index}>
          {formatBoldText(part)}
        </mark>
      );
    }
    return <span key={index}>{formatBoldText(part)}</span>;
  });
};


export const splitSummaryByFullStop = (text = "", sentencesPerPara = 3) => {
  if (!text) return [];

  const sentences = text
    .split(".")
    .map(s => s.trim())
    .filter(Boolean);

  const paragraphs = [];
  for (let i = 0; i < sentences.length; i += sentencesPerPara) {
    paragraphs.push(
      sentences.slice(i, i + sentencesPerPara).join(". ") + "."
    );
  }

  return paragraphs;
};

const drawJustifiedParagraph = (
  doc,
  text,
  x,
  y,
  maxWidth,
  lineHeight
) => {
  const words = text.split(/\s+/);
  let line = [];
  let cursorY = y;

  words.forEach((word, index) => {
    const testLine = [...line, word].join(" ");
    const testWidth = doc.getTextWidth(testLine);

    if (testWidth > maxWidth && line.length > 0) {
      const lineText = line.join(" ");
      const spaceCount = line.length - 1;

      if (spaceCount > 0) {
        const textWidth = doc.getTextWidth(lineText.replace(/\s+/g, ""));
        const extraSpace =
          (maxWidth - textWidth) / spaceCount;

        let cursorX = x;
        line.forEach((w, i) => {
          doc.text(w, cursorX, cursorY);
          cursorX += doc.getTextWidth(w);
          if (i < line.length - 1) cursorX += extraSpace;
        });
      } else {
        doc.text(lineText, x, cursorY);
      }

      cursorY += lineHeight;
      line = [word];
    } else {
      line.push(word);
    }

    if (index === words.length - 1) {
      doc.text(line.join(" "), x, cursorY);
    }
  });

  return cursorY + lineHeight;
};

function LibraryView({ items, query, onOpen, onEdit, onDelete }) {

  return (
    <div className="library-grid">
      {items.map((it, index) => {
        const coAuthors = [
          it.person2_first_name,
          it.person3_first_name,
          it.person4_first_name,
          it.person5_first_name,
        ].filter(Boolean);

        return (
          <div
            key={it.id || `library-${index}`}
            className={`library-card ${String(it.status).toLowerCase() === "inactive"
              ? "card-inactive"
              : "card-active"
              }`}
          >

            <div className="library-card-content">
              <h4
                className="library-card-title"

                onClick={() => {
                  if (String(it.status).toLowerCase() !== "inactive") {
                    onOpen && onOpen(it);
                  }
                }}
              >
                {highlightText(it.title, query)}
              </h4>

              <div className="library-card-meta">
                <strong>By:</strong>{" "}
                {it.submitter_first_name} {it.submitter_last_name}
                {coAuthors.length > 0 && (
                  <>
                    <br />
                    <strong>Co-authors:</strong>{" "}
                    {coAuthors.join(", ")}
                  </>
                )}
                <br />
                <strong>Category:</strong> {highlightText(it.category, query)}


                <br />
                <strong>Subcategory:</strong>{" "}
                {highlightText(it.sub_category, query)}
                <br />
                <strong>Year:</strong> {it.Year || it.years}
              </div>

              <div
                className="library-card-actions"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginTop: "auto",
                }}
              >
                <button
                  className="btn btn-primer btn-small"
                  onClick={() => it.status !== "inactive" && onOpen && onOpen(it)}
                  disabled={String(it.status).toLowerCase() === "inactive"}
                >
                  View Details
                </button>


                <div style={{ display: "flex", gap: "6px" }}>
                  <button
                    className="btn btn-secondary btn-small"
                    onClick={() => onEdit && onEdit(it)}
                    title="Edit entry"
                    style={{ padding: "4px 6px" }}
                  >
                    <EditIcon fontSize="small" />
                  </button>

                  {/* <button
                    className="btn btn-danger btn-small"
                    onClick={() => onDelete && onDelete(it)}
                    title="Delete entry"
                    style={{ padding: "4px 6px" }}
                  >
                    <DeleteIcon fontSize="small" />
                  </button> */}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

const SummaryModal = ({ item, onClose, query = "" }) => {
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!item) return null;

  const safeFileName = (item.title || "summary")
    .replace(/[^a-z0-9]/gi, "_")
    .slice(0, 120);

  const coAuthors = [
    item.person2_first_name,
    item.person3_first_name,
    item.person4_first_name,
    item.person5_first_name,
  ].filter(Boolean);

  const makeSafeFileName = (str = "document") =>
    str
      .toString()
      .trim()
      .replace(/[^a-z0-9_\-\. ]/gi, "_")
      .replace(/\s+/g, "_")
      .slice(0, 120);

  const loadImageDataUrl = (url, opacity = 0.12) => {
    return new Promise((resolve, reject) => {
      if (!url) return resolve(null);

      const img = new Image();
      img.crossOrigin = "anonymous";

      img.onload = () => {
        try {
          const w = img.naturalWidth;
          const h = img.naturalHeight;

          const canvas = document.createElement("canvas");
          canvas.width = w;
          canvas.height = h;

          const ctx = canvas.getContext("2d");
          ctx.clearRect(0, 0, w, h);
          ctx.globalAlpha = typeof opacity === "number" ? opacity : 0.12;
          ctx.drawImage(img, 0, 0, w, h);
          ctx.globalAlpha = 1;

          resolve({
            dataUrl: canvas.toDataURL("image/png"),
            width: w,
            height: h,
          });
        } catch (err) {
          reject(err);
        }
      };

      img.onerror = reject;
      img.src = url;
    });
  };

  const handleDownloadPdf = async (itemToSave = item) => {
    setDownloading(true);
    if (!itemToSave) return;

    // ===== Logged-in Admin Email =====
    // ===== Logged-in Admin Email (SAFE PARSE) =====

    let adminEmail = "unknown@admin";

    try {
      const keys = ["user_info", "user", "authUser", "admin"];

      for (const key of keys) {
        const data = sessionStorage.getItem(key);
        if (!data) continue;

        const parsed = JSON.parse(data);

        if (parsed?.email) {
          adminEmail = parsed.email;
          break;
        }
        if (parsed?.user?.email) {
          adminEmail = parsed.user.email;
          break;
        }
        if (parsed?.admin?.email) {
          adminEmail = parsed.admin.email;
          break;
        }
      }
    } catch (err) {
      console.warn("Admin email not found in sessionStorage");
    }


    // ===== Normalize Year =====
    const paperYear =
      itemToSave?.year ??
      itemToSave?.years ??
      itemToSave?.Year ??
      "N/A";

    // ===== Load Logo =====
    let logo = null;
    try {
      logo = await loadImageDataUrl(IEW_logo, 0.2);
    } catch {
      logo = null;
    }

    const doc = new jsPDF({ unit: "mm", format: "a4" });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;
    const maxLineWidth = pageWidth - margin * 2;
    const lineHeight = 6;

    let cursorY = margin;
    let currentPage = 1;
    const pagesWithLogo = new Set();

    const drawLogoOnPage = (pageNum) => {
      if (!logo || pagesWithLogo.has(pageNum)) return;

      doc.setPage(pageNum);
      const logoWidth = pageWidth * 0.6;
      const logoHeight = logoWidth * (logo.height / logo.width);
      const x = (pageWidth - logoWidth) / 2;
      const y = (pageHeight - logoHeight) / 2;

      doc.addImage(logo.dataUrl, "PNG", x, y, logoWidth, logoHeight);
      pagesWithLogo.add(pageNum);
    };

    const ensureLogo = () => drawLogoOnPage(currentPage);

    const checkPageBreak = () => {
      if (cursorY + lineHeight > pageHeight - margin) {
        doc.addPage();
        currentPage++;
        cursorY = margin;
        ensureLogo();
      }
    };

    // ===== Label Printer (Fixed) =====
    const addLabelValue = (label, value) => {
      if (value === undefined || value === null || value === "") return;

      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      checkPageBreak();
      doc.text(label, margin, cursorY);
      cursorY += lineHeight;

      doc.setFont("helvetica", "normal");
      const lines = doc.splitTextToSize(String(value), maxLineWidth);

      lines.forEach((line) => {
        checkPageBreak();
        doc.text(line, margin, cursorY);
        cursorY += lineHeight;
      });

      cursorY += 2;
    };

    ensureLogo();

    // ===== HEADER DATA =====
    addLabelValue("Title:", itemToSave?.title);
    addLabelValue("Category:", itemToSave?.category);
    addLabelValue("Subcategory:", itemToSave?.subcategory || itemToSave?.sub_category);
    addLabelValue("Year:", paperYear);
    addLabelValue("Author:", itemToSave?.author || itemToSave?.submitter_first_name);

    // ===== Co Authors Combine =====
    const coAuthors = [
      itemToSave?.person2_first_name,
      itemToSave?.person3_first_name,
      itemToSave?.person4_first_name,
      itemToSave?.person5_first_name,
    ]
      .filter(Boolean)
      .join(", ");

    addLabelValue("Co-Authors:", coAuthors);


    // ⭐ ADMIN EMAIL
    addLabelValue("Downloaded By:", adminEmail);

    // ===== CONTENT =====
    checkPageBreak();
    doc.line(margin, cursorY, pageWidth - margin, cursorY);
    cursorY += 8;

    doc.setFont("helvetica", "bold");
    doc.text("Content:", margin, cursorY);
    cursorY += lineHeight + 2;

    doc.setFont("helvetica", "normal");

    const contentRaw =
      itemToSave?.summary ??
      itemToSave?.abstract ??
      itemToSave?.content ??
      "No content available";

    const paragraphs = String(contentRaw).split(/\n{2,}/g);

    paragraphs.forEach((para) => {
      if (!para.trim()) return;

      const lines = doc.splitTextToSize(para.replace(/\n/g, " "), maxLineWidth);

      lines.forEach((line) => {
        checkPageBreak();
        doc.text(line, margin, cursorY);
        cursorY += lineHeight;
      });

      cursorY += 2;
    });

    // ===== File Name Safe =====
    const fileName = (itemToSave?.title || "document")
      .replace(/[^\w\s]/gi, "")
      .replace(/\s+/g, "_");

    doc.save(`${fileName}_summary.pdf`);
    setDownloading(false);
  };

  const handleDownloadFile = async (filepath) => {
    if (!filepath?.trim()) {
      alert("No file path available.");
      return;
    }

    try {
      await AbstractAPI.downloadFile(filepath);
    } catch (error) {
      console.error("Download failed:", error);
      alert("File download failed.");
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose} role="dialog" aria-modal="true">
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <header className="modal-header">
          <h2 className="modal-title">
            {highlightText(item.title || "", query)}
          </h2>
          <button
            className="modal-close"
            onClick={onClose}
            aria-label="Close summary"
          >
            <CloseIcon />
          </button>
        </header>

        <section className="modal-body">


          <div style={{ marginBottom: 16 }}>
            <p>
              <strong>Category:</strong>{" "}
              {highlightText(item.category || "", query)}
            </p>
            <p>
              <strong>Subcategory:</strong>{" "}
              {highlightText(
                item.subcategory || item.sub_category || "",
                query
              )}
            </p>
            <p>
              <strong>Year:</strong> {item.Year || item.years}
            </p>
            <p>
              <strong>Author:</strong>{" "}
              {highlightText(
                `${item.submitter_first_name || ""} ${item.submitter_last_name || ""}`,
                query
              )}

            </p>

            {coAuthors.length > 0 && (
              <p>
                <strong>Co-Authors:</strong>{" "}
                {coAuthors.map((co, idx) => (
                  <span key={idx}>
                    {highlightText(co, query)}
                    {idx < coAuthors.length - 1 && ", "}
                  </span>
                ))}
              </p>
            )}
          </div>





          <div
            className="summary-wrapper"
            role="article"
            aria-describedby="summary-content"
          >
            <div id="summary-content" className="summary-justified">
              {/* {highlightParagraphs(
                item.summarised_abstract || item.abstract || "",
                query
              )} */}
              {splitSummaryByFullStop(item.summarised_abstract || "", 3).map((para, idx) => (
                <p key={idx} className="summary-paragraph">
                  {highlightText(para, query)}
                </p>
              ))}


            </div>
          </div>
        </section>

        <footer className="modal-footer">

          <button
            className="modal-download"
            onClick={() => handleDownloadPdf(item)}
            disabled={downloading}
          >
            {downloading ? "Generating..." : "Download Full Paper"}
          </button>

          <button
            className="modal-download"
            onClick={async () => {

              const filePath =
                item.filepath ||
                item.file_path ||
                item.file ||
                item.presentation ||
                item.presentation_file;

              if (!filePath) {
                alert("No presentation uploaded for this entry.");
                return;
              }

              setDownloading(true);

              try {
                await handleDownloadFile(filePath);
              } catch (e) {
                console.error("Download failed:", e);
                alert("Download failed – check console.");
              } finally {
                setDownloading(false);
              }
            }}
            disabled={downloading}
          >
            {downloading ? "Downloading..." : "Download Presentation"}
          </button>
          {downloading && (
            <div className="download-loader">

              <p>Loading ...</p>
              <ThreeDot color="#c65100" size="medium" text="" textColor="" />
            </div>
          )}

          {/* <button
            className="modal-close-btn btn btn-primary"
            onClick={onClose}
          >
            Close
          </button> */}
        </footer>
      </div>
    </div>
  );
};

export default function AdminDashboard() {
  const [items, setItems] = useState([]);
  const [preview, setPreview] = useState(null);
  const [previewQuery, setPreviewQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(12);

  const muiFilter = createFilterOptions({
    matchFrom: "any",
    limit: 10,
    stringify: (option) =>
      typeof option === "string"
        ? option
        : option && (option.label || option.name || ""),
  });

  const navigate = useNavigate();

  const [filter, setFilter] = useState({
    q: "",
    title: "Title",
    author: "Author",
    category: "Category",
    subcategory: "Subcategory",
    year: "Year",
    status: "Status"
  });

  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [showBulkDeletePopup, setShowBulkDeletePopup] = useState(false);
  const [bulkDeleteInfo, setBulkDeleteInfo] = useState({
    message: "",
    itemsToDelete: [],
    count: 0
  });

  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");



  useEffect(() => {
    const fetchData = async () => {
      try {
        const apiResponse = await AbstractAPI.getAll();
        const data = Array.isArray(apiResponse?.data)
          ? apiResponse.data
          : Array.isArray(apiResponse)
            ? apiResponse
            : [];
        setItems(data);
      } catch (err) {
        console.error("Fetch error:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);


  const refetchData = async () => {
    try {
      const apiResponse = await AbstractAPI.getAll();
      const data = Array.isArray(apiResponse?.data)
        ? apiResponse.data
        : Array.isArray(apiResponse)
          ? apiResponse
          : [];
      setItems(data);
    } catch (err) {
      console.error("Fetch error:", err);
    }
  };


  const years = useMemo(() => {
    const set = new Set(items.map(i => i.Year || i.years).filter(Boolean));
    return ["Year", ...Array.from(set).sort((a, b) => b - a)];
  }, [items]);

  // Categories filtered by selected year
  const categories = useMemo(() => {
    let filteredItems = items;

    if (filter.year !== "Year") {
      filteredItems = filteredItems.filter(
        i => String(i.Year || i.years) === String(filter.year)
      );
    }


    const set = new Set(filteredItems.map(i => i.category).filter(Boolean));
    return ["Category", ...Array.from(set).sort()];
  }, [items, filter.year]);

  const subcategories = useMemo(() => {
    let filteredItems = items;

    if (filter.year !== "Year") {
      filteredItems = filteredItems.filter(
        i => String(i.Year || i.years) === String(filter.year)
      );
    }

    if (filter.category !== "Category") {
      filteredItems = filteredItems.filter(
        i => i.category === filter.category
      );
    }

    const set = new Set(
      filteredItems.map(i => i.subcategory || i.sub_category).filter(Boolean)
    );
    return ["Subcategory", ...Array.from(set).sort()];
  }, [items, filter.year, filter.category]);


  const authors = useMemo(() => {
    let filteredItems = items;

    if (filter.year !== "Year") {
      filteredItems = filteredItems.filter(
        i => String(i.Year || i.years) === String(filter.year)
      );
    }

    if (filter.category !== "Category") {
      filteredItems = filteredItems.filter(
        i => i.category === filter.category
      );
    }

    if (filter.subcategory !== "Subcategory") {
      filteredItems = filteredItems.filter(
        i => (i.subcategory || i.sub_category) === filter.subcategory
      );
    }

    const set = new Set(
      filteredItems
        .map(i => i.author || i.submitter_first_name)
        .filter(Boolean)
    );
    return ["Author", ...Array.from(set).sort()];
  }, [items, filter.year, filter.category, filter.subcategory]);

  const statusOptions = useMemo(() => {
    const set = new Set(items.map(i => i.status).filter(Boolean));
    return ["Status", ...Array.from(set).sort()];
  }, [items]);


  const queryOptions = useMemo(() => {
    const s = new Set();
    items.forEach(i => {
      if (!i) return;
      if (i.title) s.add(i.title);
      if (i.summary) s.add(i.summary);
      if (i.abstract) s.add(i.abstract);
      if (i.author || i.submitter_first_name)
        s.add(i.author || i.submitter_first_name);
      if (i.category) s.add(i.category);
      if (i.subcategory || i.sub_category)
        s.add(i.subcategory || i.sub_category);
      if (i.Year || i.years) s.add(String(i.Year || i.years));
    });
    return Array.from(s).slice(0, 1000);
  }, [items]);

  const filtered = useMemo(() => {
    return items.filter(i => {
      if (filter.title !== "Title" && i.title !== filter.title) return false;
      if (
        filter.author !== "Author" &&
        (i.author || i.submitter_first_name) !== filter.author
      )
        return false;
      if (filter.category !== "Category" && i.category !== filter.category)
        return false;
      if (
        filter.subcategory !== "Subcategory" &&
        (i.subcategory || i.sub_category) !== filter.subcategory
      )
        return false;
      if (
        filter.year !== "Year" &&
        String(i.Year || i.years) !== String(filter.year)
      )
        return false;

      if (
        filter.status !== "Status" &&
        String(i.status).toLowerCase() !== String(filter.status).toLowerCase()
      )
        return false;

      const q = (filter.q || "").trim().toLowerCase();
      if (!q) return true;

      return `${i.title} ${i.summary} ${i.abstract} ${i.author || i.submitter_first_name
        } ${i.category} ${i.subcategory || i.sub_category} ${i.Year || i.years
        }`
        .toLowerCase()
        .includes(q);
    });
  }, [items, filter]);

  const handleDelete = (item) => {
    setDeleteId(item.id || item.content_id);
    setShowDeletePopup(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteId) return;
    try {
      await AbstractAPI.delete(deleteId);
      refetchData();
    } catch (err) {
      console.error("Delete error:", err);
      alert("Delete failed");
    }
    setDeleteId(null);
    setShowDeletePopup(false);
  };

  const handleEdit = (item) => {
    // navigate("/fipidigilibrary/AdminForm", { state: { initial: item } });
    navigate("/AdminForm", { state: { initial: item } });
  };

  const handleOpenPreview = (item) => {
    setPreview(item);
    setPreviewQuery(filter.q || "");
  };

  const paginatedItems = useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    const end = start + rowsPerPage;
    return filtered.slice(start, end);
  }, [filtered, page, rowsPerPage]);

  const handleAddNew = () => {
    navigate("/AdminForm", { state: { initial: null } });
    // navigate("/Admin", { state: { initial: null } });
  };
  const handlelinks = () => {
    navigate("/admin-important-links ", { state: { initial: null } });
    // navigate("/Admin", { state: { initial: null } });
  };
  const handletrack = () => {
    navigate("/AdminTracking ", { state: { initial: null } });
  }
  const handleyoutubelinks = () => {
    navigate("/Adminyoutubelink ", { state: { initial: null } });
  };
  const handlegazattelinks = () => {
    navigate("/AdminGazattenotification", { state: { initial: null } });
    // navigate("/Admin", { state: { initial: null } });


  };




  const handledeleteentry = async () => {
    const isCategorySelected = filter.category !== "Category";
    const isSubcategorySelected = filter.subcategory !== "Subcategory";
    const isYearSelected = filter.year !== "Year";
    const isAuthorSelected = filter.author !== "Author";

    if (!isCategorySelected && !isSubcategorySelected && !isYearSelected && !isAuthorSelected) {
      setErrorMessage("Please select a Category, Subcategory, Year, or Author to delete entries.");
      setShowErrorPopup(true);
      return;
    }

    let deleteMessage = "";
    let itemsToDelete = [];

    itemsToDelete = [...filtered];
    const filterParts = [];
    if (isYearSelected) filterParts.push(`year "${filter.year}"`);
    if (isCategorySelected) filterParts.push(`category "${filter.category}"`);
    if (isSubcategorySelected) filterParts.push(`subcategory "${filter.subcategory}"`);
    if (isAuthorSelected) filterParts.push(`author "${filter.author}"`);

    if (filterParts.length === 1) {
      deleteMessage = `Are you sure you want to delete all entries in ${filterParts[0]}?`;
    } else if (filterParts.length === 2) {
      deleteMessage = `Are you sure you want to delete all entries in ${filterParts[0]} and ${filterParts[1]}?`;
    } else {
      deleteMessage = `Are you sure you want to delete all entries matching the selected filters (${filterParts.join(", ")})?`;
    }

    if (itemsToDelete.length === 0) {
      setErrorMessage("No entries found matching the selected filters.");
      setShowErrorPopup(true);
      return;
    }

    // Set the bulk delete info and show popup
    setBulkDeleteInfo({
      message: deleteMessage,
      itemsToDelete: itemsToDelete,
      count: itemsToDelete.length
    });
    setShowBulkDeletePopup(true);
  };


  const handleConfirmBulkDelete = async () => {
    try {
      setLoading(true);
      setShowBulkDeletePopup(false);

      const deletePromises = bulkDeleteInfo.itemsToDelete.map(item =>
        AbstractAPI.delete(item.id || item.content_id)
      );

      await Promise.all(deletePromises);

      alert(
        `Successfully deleted ${bulkDeleteInfo.count} ${bulkDeleteInfo.count === 1 ? "entry" : "entries"
        }.`
      );

      await refetchData();

      setFilter({
        q: "",
        title: "Title",
        author: "Author",
        category: "Category",
        subcategory: "Subcategory",
        year: "Year",
      });
      setPage(1);
    } catch (err) {
      console.error("Bulk delete error:", err);
      alert("Failed to delete some entries. Please try again.");
    } finally {
      setLoading(false);
      setBulkDeleteInfo({ message: "", itemsToDelete: [], count: 0 });
    }
  };

  const handleFilterChange = (filterType, value) => {
    setPage(1);

    if (filterType === "year") {
      setFilter(f => ({
        ...f,
        year: value,
        category: "Category",
        subcategory: "Subcategory",
        author: "Author",
      }));
    } else if (filterType === "category") {
      setFilter(f => ({
        ...f,
        category: value,
        subcategory: "Subcategory",
        author: "Author",
      }));
    } else if (filterType === "subcategory") {
      setFilter(f => ({
        ...f,
        subcategory: value,
        author: "Author",
      }));
    } else {
      setFilter(f => ({ ...f, [filterType]: value }));
    }
  };


  const handleClearFilters = () => {
    setFilter({
      q: "",
      title: "Title",
      author: "Author",
      category: "Category",
      subcategory: "Subcategory",
      year: "Year",
    });
    setPage(1);
  };

  const showAllFilter = (options, state) => {
    if (!state.inputValue) return options;
    return options.filter(opt =>
      String(opt).toLowerCase().includes(state.inputValue.toLowerCase())
    );
  };

  if (loading) {
    return (
      <main className="main-content">
        <div className="container-iew-container">
          <div
            style={{
              padding: "48px",
              textAlign: "center",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "12px",
            }}
          >
            <p>Loading ...</p>
            <ThreeDot
              color="#c65100"
              size="medium"
              text=""
              textColor=""
            />
          </div>
        </div>
      </main>
    );
  }

  return (
    <div className="admin-container">
      <div className="dashboard-header">
        <h2>Admin Dashboard</h2>

        <div className="link-btn-group">



          <button className="btn btn-primary add-btn" onClick={handletrack}>
            Tracking
          </button>
          <button className="btn btn-primary add-btn" onClick={handlelinks}>
            <AddIcon /> Add Link
          </button>
          <button className="btn btn-primary add-btn" onClick={handleyoutubelinks}>
            <AddIcon /> Add YouTube Video
          </button>

          <button className="btn btn-primary add-btn" onClick={handlegazattelinks}>
            <AddIcon /> Add Gazette
          </button>
        </div>

      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginLeft: "20px"
        }}
      >
        <button
          onClick={() => navigate("/")}
          className="clear-filters-btn"
        >
          ← Back
        </button>
      </div>


      <div className="controls-bar">

        <button className="btn btn-primary" onClick={handleAddNew}>
          <AddIcon /> Add
        </button>
        <Autocomplete
          size="small"
          freeSolo
          openOnFocus
          clearOnEscape
          options={queryOptions}
          inputValue={filter.q}
          onInputChange={(_e, v) => setFilter(f => ({ ...f, q: v || "" }))}
          filterOptions={(options, state) => muiFilter(options, state)}
          getOptionLabel={(option) => (option == null ? "" : String(option))}
          noOptionsText="No suggestions"
          sx={{ width: 250 }}
          renderInput={(params) => (
            <TextField {...params} placeholder="Search entries..." />
          )}
        />

        <Autocomplete
          options={years}
          value={filter.year}
          openOnFocus
          filterOptions={showAllFilter}
          onChange={(_, v) => handleFilterChange("year", v || "Year")}
          getOptionLabel={(o) => String(o)}
          sx={{ width: 120 }}
          renderInput={(params) => (
            <TextField {...params} label="Year" size="small" />
          )}
        />

        <Autocomplete
          options={categories}
          value={filter.category}
          openOnFocus
          filterOptions={showAllFilter}
          onChange={(_, v) =>
            handleFilterChange("category", v || "Category")
          }
          getOptionLabel={(o) => String(o)}
          sx={{ width: 160 }}
          renderInput={(params) => (
            <TextField {...params} label="Category" size="small" />
          )}
        />

        <Autocomplete
          options={subcategories}
          value={filter.subcategory}
          openOnFocus
          filterOptions={showAllFilter}
          onChange={(_, v) =>
            handleFilterChange("subcategory", v || "Subcategory")
          }
          getOptionLabel={(o) => String(o)}
          sx={{ width: 200 }}
          renderInput={(params) => (
            <TextField {...params} label="Subcategory" size="small" />
          )}
        />

        <Autocomplete
          options={authors}
          value={filter.author}
          openOnFocus
          filterOptions={showAllFilter}
          onChange={(_, v) =>
            handleFilterChange("author", v || "Author")
          }
          getOptionLabel={(o) => String(o)}
          sx={{ width: 160 }}
          renderInput={(params) => (
            <TextField {...params} label="Author" size="small" />
          )}
        />
        <Autocomplete
          options={statusOptions}
          value={filter.status}
          openOnFocus
          filterOptions={showAllFilter}
          onChange={(_, v) =>
            handleFilterChange("status", v || "Status")
          }
          getOptionLabel={(o) => String(o)}
          sx={{ width: 140 }}
          renderInput={(params) => (
            <TextField {...params} label="Status" size="small" />
          )}
        />

        <button className="clear-filters-btn" onClick={handleClearFilters}>
          Clear
        </button>

        {/* <button className="btn btn-danger" onClick={handledeleteentry}>
          <DeleteIcon />
        </button> */}
      </div>

      <LibraryView
        items={paginatedItems}
        query={filter.q}
        onOpen={handleOpenPreview}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />


      {filtered.length > rowsPerPage && (
        <div
          style={{ display: "flex", justifyContent: "center", marginTop: 16 }}
        >
          <Pagination
            count={Math.ceil(filtered.length / rowsPerPage)}
            page={page}
            onChange={(event, value) => setPage(value)}
            color="primary"
          />
        </div>
      )}

      {preview && (
        <SummaryModal
          item={preview}
          query={previewQuery}
          onClose={() => {
            setPreview(null);
            setPreviewQuery("");
          }}

        />
      )}

      {/* Single Delete Popup */}
      <CustomPopup
        open={showDeletePopup}
        onClose={() => setShowDeletePopup(false)}
        title="Delete this entry?"
      >
        <div style={{ padding: 16 }}>
          <p>Are you sure you want to delete this entry?</p>
          <div
            style={{
              display: "flex",
              gap: 8,
              justifyContent: "space-around",
              marginTop: 12,
            }}
          >
            <button className="btn btn-danger" onClick={handleConfirmDelete}>
              Delete
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => setShowDeletePopup(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      </CustomPopup>

      {/* Bulk Delete Popup */}
      <CustomPopup
        open={showBulkDeletePopup}
        onClose={() => setShowBulkDeletePopup(false)}
        title="Bulk Delete Entries"
      >
        <div style={{ padding: 16 }}>
          <p style={{ marginBottom: 12 }}>{bulkDeleteInfo.message}</p>
          <p style={{ fontWeight: 'bold', marginBottom: 8 }}>
            This will delete {bulkDeleteInfo.count}{" "}
            {bulkDeleteInfo.count === 1 ? "entry" : "entries"}.
          </p>
          <p style={{ color: '#d32f2f', fontSize: '14px' }}>
            This action cannot be undone.
          </p>
          <div
            style={{
              display: "flex",
              gap: 8,
              justifyContent: "space-around",
              marginTop: 16,
            }}
          >
            <button className="btn btn-danger" onClick={handleConfirmBulkDelete}>
              Delete All
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => setShowBulkDeletePopup(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      </CustomPopup>

      <CustomPopup
        open={showErrorPopup}
        onClose={() => setShowErrorPopup(false)}
        title="Error"
      >
        <div style={{ padding: 16 }}>
          <p style={{ marginBottom: 16 }}>{errorMessage}</p>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
            }}
          >
            <button
              className="btn btn-danger"
              onClick={() => setShowErrorPopup(false)}
            >
              OK
            </button>
          </div>
        </div>
      </CustomPopup>
    </div>
  );
}